import { useState } from 'react';
import type { FormEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button, ButtonWithLoading } from '@actual-app/components/button';
import { View } from '@actual-app/components/view';

import type {
  FinancialOnboardingState,
  ProductFinanceClient,
} from '#components/product-finance/client';
import {
  formatProductMoney,
  getDeviceLocalDate,
  parseMoneyInput,
} from '#components/product-finance/money';

import {
  actionStyle,
  assertNever,
  Field,
  FormError,
  formStyle,
  getFinancialErrorMessage,
  helpStyle,
  inputStyle,
  SourceList,
  StepCard,
} from './onboarding-shared';

export type RecurrenceFrequency = 'weekly' | 'monthly' | 'yearly';

export function IncomeStep({
  onboarding,
  client,
  onCreated,
  onContinue,
}: Readonly<{
  onboarding: FinancialOnboardingState;
  client: ProductFinanceClient;
  onCreated: () => Promise<unknown>;
  onContinue: () => void;
}>) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [frequency, setFrequency] = useState<RecurrenceFrequency>('monthly');
  const [dayOfWeek, setDayOfWeek] = useState('1');
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [month, setMonth] = useState('1');
  const [startDate, setStartDate] = useState(getDeviceLocalDate());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const amountMinorUnits = parseMoneyInput(amount);
    if (name.trim().length === 0 || amountMinorUnits === null) {
      setError(t('Enter an income name and a positive amount.'));
      return;
    }
    setError(null);
    setIsSubmitting(true);
    try {
      await client.createFixedIncomeTemplate({
        name: name.trim(),
        amountMinorUnits,
        currency: onboarding.settings?.currency ?? 'GTQ',
        recurrence: createRecurrenceRequest(
          frequency,
          dayOfWeek,
          dayOfMonth,
          month,
        ),
        startDate,
        enabled: true,
      });
      setName('');
      setAmount('');
      await onCreated();
    } catch (submitError) {
      setError(getFinancialErrorMessage(submitError, t));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <StepCard title={<Trans>Add your fixed incomes</Trans>}>
      <p style={helpStyle}>
        <Trans>
          Add one or more reliable incomes. Variable and extraordinary income
          are intentionally excluded from budget-cycle anchors.
        </Trans>
      </p>
      <SourceList
        empty={<Trans>No fixed incomes have been added yet.</Trans>}
        items={onboarding.fixedIncomeTemplates.map(template => ({
          id: template.id,
          name: template.name,
          detail: formatProductMoney(template.amount),
        }))}
      />
      <form onSubmit={submit} style={formStyle}>
        <Field label={<Trans>Income name</Trans>} htmlFor="income-name">
          <input
            id="income-name"
            value={name}
            onChange={event => setName(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label={<Trans>Amount</Trans>} htmlFor="income-amount">
          <input
            id="income-amount"
            inputMode="decimal"
            value={amount}
            onChange={event => setAmount(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <Field label={<Trans>Frequency</Trans>} htmlFor="income-frequency">
          <select
            id="income-frequency"
            aria-label={t('Frequency')}
            value={frequency}
            onChange={event =>
              setFrequency(event.target.value as RecurrenceFrequency)
            }
            style={inputStyle}
          >
            <option value="weekly">
              <Trans>Weekly</Trans>
            </option>
            <option value="monthly">
              <Trans>Monthly</Trans>
            </option>
            <option value="yearly">
              <Trans>Yearly</Trans>
            </option>
          </select>
        </Field>
        {frequency === 'weekly' && (
          <Field label={<Trans>Day of week</Trans>} htmlFor="income-weekday">
            <select
              id="income-weekday"
              value={dayOfWeek}
              onChange={event => setDayOfWeek(event.target.value)}
              style={inputStyle}
            >
              {[
                t('Monday'),
                t('Tuesday'),
                t('Wednesday'),
                t('Thursday'),
                t('Friday'),
                t('Saturday'),
                t('Sunday'),
              ].map((label, index) => (
                <option key={label} value={index + 1}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        )}
        {frequency !== 'weekly' && (
          <Field label={<Trans>Day of month</Trans>} htmlFor="income-day">
            <input
              id="income-day"
              type="number"
              min={1}
              max={31}
              value={dayOfMonth}
              onChange={event => setDayOfMonth(event.target.value)}
              style={inputStyle}
            />
          </Field>
        )}
        {frequency === 'yearly' && (
          <Field label={<Trans>Month</Trans>} htmlFor="income-month">
            <input
              id="income-month"
              type="number"
              min={1}
              max={12}
              value={month}
              onChange={event => setMonth(event.target.value)}
              style={inputStyle}
            />
          </Field>
        )}
        {frequency === 'monthly' && (
          <p style={helpStyle}>
            <Trans>
              Day 31 means the last available day in shorter months.
            </Trans>
          </p>
        )}
        <Field label={<Trans>Starts on</Trans>} htmlFor="income-start-date">
          <input
            id="income-start-date"
            type="date"
            value={startDate}
            onChange={event => setStartDate(event.target.value)}
            style={inputStyle}
          />
        </Field>
        <FormError message={error} />
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8 }}>
          <ButtonWithLoading
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            style={actionStyle}
          >
            <Trans>Add fixed income</Trans>
          </ButtonWithLoading>
          <Button
            type="button"
            isDisabled={onboarding.fixedIncomeTemplates.length === 0}
            onPress={onContinue}
            style={actionStyle}
          >
            <Trans>Continue to budget cycles</Trans>
          </Button>
        </View>
      </form>
    </StepCard>
  );
}

export function createRecurrenceRequest(
  frequency: RecurrenceFrequency,
  dayOfWeek: string,
  dayOfMonth: string,
  month: string,
) {
  switch (frequency) {
    case 'weekly':
      return { frequency, dayOfWeek: Number(dayOfWeek) } as const;
    case 'monthly':
      return { frequency, dayOfMonth: Number(dayOfMonth) } as const;
    case 'yearly':
      return {
        frequency,
        month: Number(month),
        dayOfMonth: Number(dayOfMonth),
      } as const;
    default:
      return assertNever(frequency);
  }
}
